import LeaseApiService from './apiService';
import { endPoint } from '../EndPoints/endPoint';

/**
 * @typedef {{ value: string, text: string }} DropdownItem
 * @typedef {{ unitNo: string, leaseNo: string, unitClass: string }} UnitItem
 * @typedef {{ leaseNo: string, groupId: string }} LeaseItem
 * @typedef {{ accountCode: string, accountName: string }} DebitAccountItem
 * @typedef {{ leaseNo: string, tenantNo: string, tenantName: string, contactNo: string, agent: string, groupId: string,
 *   propertyNo: string, unitNo: string, mtcNo: string, leaseFrom: string, leaseTo: string, status: string, hasAccess: boolean }} TenantDetails
 * @typedef {{ receiptNo: string, leaseNo: string, tenantNo: string, tenantName: string, propertyNo: string, propertyName: string,
 *   unitNo: string, transactionType: string, paymentDate: string, paymentDateValue: string, chequeNo: string, chequeDate: string,
 *   drawnOnBank: string, debitAccountCode: string, debitAccountName: string, refNo: string, narration: string, lineCount: number,
 *   totalAmount: number, modifiedBy: string, totalRecords: number }} ReceiptRow
 * @typedef {{ receiptNo?: string, leaseNo?: string, tenantNo?: string, propertyNo?: string, unitNo?: string, transactionType?: string,
 *   paymentTypeDesc?: string, fromDate?: string, toDate?: string, pageNo?: number, pageSize?: number }} ReceiptFilters
 * @typedef {{ receiptNo: string, leaseNo: string, groupId: string, tenantNo: string, tenantName: string, propertyNo: string,
 *   propertyName: string, unitNo: string, receiptType: string, transactionType: string, paymentDate: string, chequeNo: string,
 *   chequeDate: string, drawnOnBank: string, cardType: string, cardNo: string, debitAccountCode: string, debitAccountName: string,
 *   refNo: string, narration: string, voucherNo: string, totalAmount: number, modifiedBy: string, modifiedOn: string }} ReceiptHeader
 * @typedef {{ recordId: string, paymentTypeDesc: string, amount: number, creditAccountCode: string, creditAccountName: string }} ReceiptDetailLine
 * @typedef {{ header: ReceiptHeader, lines: ReceiptDetailLine[] }} ReceiptDetail
 * @typedef {{ propertyNo: string, unitNo: string, leaseNo: string, tenantNo: string, paymentTypeDesc: string,
 *   creditAccountCode: string, amount: number }} CreateReceiptLine
 * @typedef {{ receiptDate: string, transactionType: string, debitAccountCode: string, chequeNo: string|null, drawnOnBank: string|null,
 *   chequeDate: string|null, totalAmount: number, refNo: string|null, narration: string, lines: CreateReceiptLine[] }} CreateReceiptRequest
 * @typedef {{ receiptNo: string, lineCount: number, totalAmount: number }} CreateReceiptResult
 * @typedef {{ ok: boolean, status: number, message: string, result: any, dropdowns: any, pagination: any }} ApiResult
 */

const api = new LeaseApiService();
const http = api.axiosInstance;

const GENERIC_ERROR = 'Something went wrong. Please try again.';
export const NOT_FOUND_MESSAGE = "Not found or you don't have access to it.";
// The backend allows up to 20 minutes for a save; only that request gets this timeout.
export const CREATE_TIMEOUT_MS = 20 * 60 * 1000;

/** 400 carries a readable message; 404 and everything else (500, network) get fixed messages. */
const errorMessage = (status, serverMessage) => {
    if (status === 400) return serverMessage || 'Invalid request.';
    if (status === 404) return NOT_FOUND_MESSAGE;
    return GENERIC_ERROR;
};

/** Normalises BaseResponse / PaginationResponse success and failure so callers never touch axios errors. */
const call = async (request) => {
    try {
        const res = await request();
        const body = res?.data;
        const status = res?.status === 204 ? 204 : (body?.statusCode ?? res?.status);
        const ok = status >= 200 && status < 300;
        return {
            ok,
            status,
            message: ok ? body?.message || '' : errorMessage(status, body?.message),
            result: body?.result ?? body?.data ?? null,
            dropdowns: body?.dropdowns ?? null,
            pagination: body?.paginationData ?? null,
        };
    } catch (error) {
        const status = error?.response?.status || 0;
        return { ok: false, status, message: errorMessage(status, error?.response?.data?.message), result: null, dropdowns: null, pagination: null };
    }
};

/** Leaves out empty filters. */
const withQuery = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && `${value}`.trim() !== '') q.append(key, `${value}`.trim());
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

// Dropdowns are loaded once and kept for the session; a failed load is not cached.
const dropdownCache = new Map();

/** @param {string[]} dropdownTypes */
export const getDropdowns = (dropdownTypes) => {
    const key = [...dropdownTypes].sort().join(',');
    if (!dropdownCache.has(key)) {
        const pending = call(() => http.post(endPoint.MRA_DROPDOWNS, { dropdownTypes })).then((res) => {
            if (!res.ok) dropdownCache.delete(key);
            return res;
        });
        dropdownCache.set(key, pending);
    }
    return dropdownCache.get(key);
};

export const getUnits = ({ propertyNo, showAll = false }) =>
    call(() => http.get(endPoint.MRA_UNITS + withQuery({ propertyNo, showAll })));

export const getLeases = ({ propertyNo, showAll = false }) =>
    call(() => http.get(endPoint.MRA_LEASES + withQuery({ propertyNo, showAll })));

/** The first item returned is the default account for that method and property. */
export const getDebitAccounts = ({ transactionType, propertyNo }) =>
    call(() => http.get(endPoint.MRA_DEBIT_ACCOUNTS + withQuery({ transactionType, propertyNo })));

/** Pass leaseNo, or propertyNo + unitNo, or tenantNo. */
export const getTenantDetails = ({ leaseNo, propertyNo, unitNo, tenantNo }) =>
    call(() => http.get(endPoint.MRA_TENANT_DETAILS + withQuery({ leaseNo, propertyNo, unitNo, tenantNo })));

/** result is true when a misc receipt already exists for that lease and payment type (warning only). */
export const checkDuplicate = ({ leaseNo, paymentTypeDesc }) =>
    call(() => http.get(endPoint.MRA_CHECK_DUPLICATE + withQuery({ leaseNo, paymentTypeDesc })));

/** @param {ReceiptFilters} filters */
export const getReceipts = (filters = {}) => call(() => http.get(endPoint.MRA_LIST + withQuery(filters)));

export const getReceiptByNo = (receiptNo) => call(() => http.get(`${endPoint.MRA_BY_NO}${encodeURIComponent(receiptNo)}`));

/** @param {CreateReceiptRequest} body */
export const createReceipt = (body) => call(() => http.post(endPoint.MRA_CREATE, body, { timeout: CREATE_TIMEOUT_MS }));

const miscellaneousReceiptRAApi = {
    getDropdowns, getUnits, getLeases, getDebitAccounts, getTenantDetails, checkDuplicate, getReceipts, getReceiptByNo, createReceipt,
};

export default miscellaneousReceiptRAApi;
